import { Box, CssBaseline, IconButton, Link, Stack, Typography } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useEffect, useState } from "react";
import { createGlobalStyle } from "styled-components";
import AppTheme from "../../../theme/AppTheme";
import LoginMarketingCarousel from "../../components/LoginMarketingCarousel";
import PartnerLoginFaqDialog from "../../components/PartnerLoginFaqDialog";
import { PARTNER_PRIMARY_HEX as PRIMARY } from "../../../constants/partnerBrand";
import { useSellerOrgConfig } from "../../../Hooks/useSellerOrgConfig";

const SURFACE = "#f9f9ff";
const ON_SURFACE_VARIANT = "#52525b";

const IOS_BADGE =
  "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1276560000";
const PLAY_BADGE =
  "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png";

const GlobalPartnerFonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
`;

const TERMS_URL =
  "https://sqc-bucket.in-maa-1.linodeobjects.com/Policies%2FValarDigital%20Commerce%20-%20Seller%20Terms%20%26%20Conditions.pdf";
const PRIVACY_URL =
  "https://sqc-bucket.in-maa-1.linodeobjects.com/Policies%2FValar%20Privacy%20Policy.pdf";

const DEFAULT_TAZTY_IOS_STORE_URL =
  "https://apps.apple.com/in/app/tazty-restaurant-partner/id6760400213";
const DEFAULT_TAZTY_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=in.tazty.seller";

const iosStoreUrl =
  process.env.REACT_APP_TAZTY_IOS_STORE_URL?.replace(/"/g, "").trim() || DEFAULT_TAZTY_IOS_STORE_URL;
const playStoreUrl =
  process.env.REACT_APP_TAZTY_PLAY_STORE_URL?.replace(/"/g, "").trim() || DEFAULT_TAZTY_PLAY_STORE_URL;

const DEFAULT_TAZTY_CONSUMER_IOS_STORE_URL =
  "https://apps.apple.com/in/app/tazty-food-delivery/id6759788648";
const DEFAULT_TAZTY_CONSUMER_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=in.tazty.buyer";

const consumerIosStoreUrl =
  process.env.REACT_APP_TAZTY_CONSUMER_IOS_STORE_URL?.replace(/"/g, "").trim() ||
  DEFAULT_TAZTY_CONSUMER_IOS_STORE_URL;
const consumerPlayStoreUrl =
  process.env.REACT_APP_TAZTY_CONSUMER_PLAY_STORE_URL?.replace(/"/g, "").trim() ||
  DEFAULT_TAZTY_CONSUMER_PLAY_STORE_URL;

const showConsumerAppStrip = Boolean(consumerIosStoreUrl || consumerPlayStoreUrl);

const DEFAULT_TAZTY_INSTAGRAM_URL = "https://www.instagram.com/tazty.app";

const instagramUrl =
  process.env.REACT_APP_TAZTY_INSTAGRAM_URL?.replace(/"/g, "").trim() || DEFAULT_TAZTY_INSTAGRAM_URL;
const DEFAULT_TAZTY_FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61584976314803";

const facebookUrl =
  process.env.REACT_APP_TAZTY_FACEBOOK_URL?.replace(/"/g, "").trim() || DEFAULT_TAZTY_FACEBOOK_URL;

interface PartnerPortalSignInLayoutProps {
  children: React.ReactNode;
  disableCustomTheme?: boolean;
}

const PartnerPortalSignInLayout: React.FC<PartnerPortalSignInLayoutProps> = ({
  children,
  disableCustomTheme,
}) => {
  const [faqOpen, setFaqOpen] = useState(false);
  const { branding } = useSellerOrgConfig();
  const hasLogo = Boolean(branding.authHeaderLogo);
  const hasWhatsapp = Boolean(branding.whatsappHref);

  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === "#faq") setFaqOpen(true);
    };
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyHeight = body.style.height;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.height = "100%";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.height = prevHtmlHeight;
      body.style.height = prevBodyHeight;
    };
  }, []);

  return (
    <AppTheme disableCustomTheme={disableCustomTheme}>
      <CssBaseline enableColorScheme />
      <GlobalPartnerFonts />
      <Box
        sx={{
          fontFamily: '"Inter", "Plus Jakarta Sans", system-ui, sans-serif',
          bgcolor: SURFACE,
          color: "#151c27",
          height: "100dvh",
          maxHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          component="header"
          sx={{
            height: { xs: 56, sm: 62 },
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            /* Edge inset for corner logos; height fits larger partner + store badges */
            pl: { xs: 1.5, sm: 2, md: 3 },
            pr: { xs: 1, sm: 2, md: 2.5 },
            bgcolor: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(12px)",
            zIndex: 50,
            borderBottom: "1px solid rgba(0,0,0,0.04)",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            {hasLogo ? (
              <Box
                component="img"
                src={branding.authHeaderLogo}
                alt={branding.authHeaderLogoAlt}
                sx={{
                  width: { xs: 92, sm: 112, md: 128 },
                  height: "auto",
                  maxHeight: { xs: 44, sm: 50 },
                  objectFit: "contain",
                  flexShrink: 0,
                  ml: { xs: -0.25, sm: 0 },
                }}
              />
            ) : (
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: ON_SURFACE_VARIANT, flexShrink: 0 }}
              >
                {branding.authHeaderLogoAlt}
              </Typography>
            )}

            <Stack
              direction="row"
              spacing={{ xs: 0.75, sm: 1.25 }}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="flex-end"
              sx={{ flexShrink: 0, mr: { xs: -0.25, sm: 0 } }}
            >
              <Link
                href={iosStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ lineHeight: 0, "&::before": { display: "none" } }}
              >
                <Box
                  component="img"
                  src={IOS_BADGE}
                  alt="Download on the App Store"
                  sx={{ display: "block", height: { xs: 30, sm: 34 }, width: "auto", maxWidth: "100%" }}
                />
              </Link>
              <Link
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ lineHeight: 0, "&::before": { display: "none" } }}
              >
                <Box
                  component="img"
                  src={PLAY_BADGE}
                  alt="Get it on Google Play"
                  sx={{ display: "block", height: { xs: 40, sm: 44 }, width: "auto", maxWidth: 152 }}
                />
              </Link>
            </Stack>
          </Stack>
        </Box>

        <Box
          component="main"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            id="partner-marketing-panel"
            sx={{
              display: { xs: "none", lg: "flex" },
              flex: { lg: "1 1 60%" },
              flexDirection: "column",
              width: { lg: "60%" },
              minWidth: 0,
              minHeight: 0,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", height: "100%" }}>
              {/* Tazty Direct partner shell only — carousel is not used for Valar/ONDC */}
              <LoginMarketingCarousel />
            </Box>
          </Box>

          <Box
            id="partner-login-form-panel"
            sx={{
              /* Mobile: grow so the card can sit vertically centered in the area below the header */
              flex: { xs: "1 1 0%", lg: "1 1 40%" },
              width: { xs: "100%", lg: "40%" },
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, sm: 3, md: 4 },
              bgcolor: SURFACE,
              overflow: { xs: "auto", lg: "hidden" },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 440,
                bgcolor: "#fff",
                borderRadius: "30px",
                boxShadow: "0 32px 64px -16px rgba(0,110,47,0.12)",
                border: "1px solid rgba(188,203,185,0.15)",
                overflow: "hidden",
                p: { xs: 3, sm: 4, md: 5 },
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>

        <Box
          component="footer"
          sx={{
            flexShrink: 0,
            minHeight: { xs: "auto", md: 56 },
            display: "flex",
            alignItems: "center",
            pl: { xs: 1.5, sm: 2, md: 3 },
            pr: { xs: 1, sm: 2, md: 2.5 },
            py: { xs: 1.25, md: 1 },
            bgcolor: "#fff",
            borderTop: "1px solid rgba(188,203,185,0.25)",
            zIndex: 50,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "center", md: "center" }}
            justifyContent={{ xs: "center", md: "space-between" }}
            spacing={{ xs: 1.5, md: 0 }}
            sx={{ width: "100%" }}
          >
            <Stack
              direction="column"
              spacing={1}
              alignItems={{ xs: "center", md: "flex-start" }}
              sx={{ flexShrink: 0, ml: { xs: 0, sm: 0 }, maxWidth: { xs: "100%", md: 360 } }}
            >
              <Stack
                direction="row"
                spacing={3}
                flexWrap="wrap"
                useFlexGap
                justifyContent={{ xs: "center", md: "flex-start" }}
                sx={{ rowGap: 0.5 }}
              >
                <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" underline="always" sx={footerLinkSx}>
                  Privacy
                </Link>
                <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer" underline="always" sx={footerLinkSx}>
                  Terms
                </Link>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setFaqOpen(true)}
                  sx={footerNavActionSx}
                >
                  FAQs
                </Box>
              </Stack>

              {showConsumerAppStrip ? (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 0.75, sm: 1.25 }}
                  alignItems="center"
                  justifyContent={{ xs: "center", md: "flex-start" }}
                  sx={{
                    pt: { xs: 0.5, md: 0.5 },
                    mt: { xs: 0, md: 0 },
                    borderTop: "1px solid rgba(188,203,185,0.2)",
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  <Typography component="span" sx={footerConsumerLabelSx}>
                    Tazty — food delivery app
                  </Typography>
                  <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
                    {consumerIosStoreUrl ? (
                      <Link
                        href={consumerIosStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ lineHeight: 0, "&::before": { display: "none" } }}
                      >
                        <Box
                          component="img"
                          src={IOS_BADGE}
                          alt="Download Tazty food delivery on the App Store"
                          sx={{
                            display: "block",
                            height: { xs: 24, sm: 26 },
                            width: "auto",
                            maxWidth: "100%",
                          }}
                        />
                      </Link>
                    ) : null}
                    {consumerPlayStoreUrl ? (
                      <Link
                        href={consumerPlayStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ lineHeight: 0, "&::before": { display: "none" } }}
                      >
                        <Box
                          component="img"
                          src={PLAY_BADGE}
                          alt="Get Tazty food delivery on Google Play"
                          sx={{
                            display: "block",
                            height: { xs: 32, sm: 34 },
                            width: "auto",
                            maxWidth: 128,
                          }}
                        />
                      </Link>
                    ) : null}
                  </Stack>
                </Stack>
              ) : null}
            </Stack>

            <Typography
              sx={{
                ...footerMetaSx,
                order: { xs: 1, md: 0 },
                textAlign: { xs: "center", md: "center" },
                flex: { md: 1 },
                px: { xs: 1, md: 1 },
                alignSelf: { xs: "center", md: "center" },
                maxWidth: { xs: 360, md: "none" },
                lineHeight: 1.45,
              }}
            >
              Valar Digital Commerce Private Limited © {new Date().getFullYear()} · All rights reserved
            </Typography>

            <Stack
              direction="row"
              spacing={0.25}
              alignItems="center"
              justifyContent="center"
              sx={{
                flexShrink: 0,
                mr: { xs: 0, sm: -0.25 },
                alignSelf: { xs: "center", md: "center" },
                order: { xs: 2, md: 0 },
              }}
            >
              {hasWhatsapp && (
                <IconButton
                  component="a"
                  href={branding.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  size="small"
                  sx={{
                    color: "#25D366",
                    "&:hover": { color: "#128C7E", bgcolor: "transparent" },
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 24 }} />
                </IconButton>
              )}
              <IconButton
                component="a"
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                size="small"
                sx={{
                  color: ON_SURFACE_VARIANT,
                  "&:hover": { color: PRIMARY, bgcolor: "transparent" },
                }}
              >
                <InstagramIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                component="a"
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                size="small"
                sx={{
                  color: ON_SURFACE_VARIANT,
                  "&:hover": { color: PRIMARY, bgcolor: "transparent" },
                }}
              >
                <FacebookIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        <PartnerLoginFaqDialog open={faqOpen} onClose={() => setFaqOpen(false)} />
      </Box>
    </AppTheme>
  );
};

/** Shared footer nav label (Privacy, Terms, FAQ). */
const footerLinkSx = {
  fontSize: "10px",
  fontWeight: 400,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: ON_SURFACE_VARIANT,
  textDecoration: "underline",
  textUnderlineOffset: "0.18em",
  textDecorationColor: "currentcolor",
  "&:hover": { color: PRIMARY, textDecoration: "underline" },
};

/**
 * Same look as `Link` nav items; avoid `font: inherit` (it resets `letter-spacing`).
 * Native `<button>` reset only.
 */
const footerNavActionSx = {
  ...footerLinkSx,
  cursor: "pointer",
  border: "none",
  background: "none",
  padding: 0,
  margin: 0,
  fontFamily: "inherit",
  fontSize: "10px",
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: "0.12em",
  WebkitAppearance: "none",
  MozAppearance: "none",
};

const footerMetaSx = {
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: ON_SURFACE_VARIANT,
};

const footerConsumerLabelSx = {
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.02em",
  color: ON_SURFACE_VARIANT,
  lineHeight: 1.2,
  display: "inline-flex",
  alignItems: "center",
  textAlign: { xs: "center", md: "left" } as const,
  whiteSpace: { xs: "normal", sm: "nowrap" } as const,
};

export default PartnerPortalSignInLayout;
